import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosApiInstance from '../../api/axiosClient';
import type { InboxItem, InboxResponse, NotificationRecipient, SendNotificationPayload } from './types';

const inboxKey = ['notifications', 'inbox'] as const;
const unreadKey = ['notifications', 'unread-count'] as const;
const recipientsKey = ['notifications', 'recipients'] as const;

function patchInboxCache(
    qc: ReturnType<typeof useQueryClient>,
    limit: number,
    patch: (items: InboxItem[]) => InboxItem[],
) {
    qc.setQueriesData<InboxResponse>({ queryKey: [...inboxKey, limit] }, (old) => {
        if (!old) return old;
        return { ...old, items: patch(old.items) };
    });
    qc.setQueriesData<InboxResponse>({ queryKey: inboxKey }, (old) => {
        if (!old) return old;
        return { ...old, items: patch(old.items) };
    });
}

export function useUnreadCountQuery(enabled = true) {
    return useQuery({
        queryKey: unreadKey,
        enabled,
        queryFn: async () => {
            const { data } = await axiosApiInstance.get<{ count: number }>('/api/notifications/unread-count');
            return data.count;
        },
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
    });
}

export function useInboxQuery(limit = 20, enabled = true) {
    return useQuery({
        queryKey: [...inboxKey, limit],
        enabled,
        queryFn: async () => {
            const { data } = await axiosApiInstance.get<InboxResponse>(
                `/api/notifications/?limit=${limit}`,
            );
            return data;
        },
    });
}

export function useMarkNotificationReadMutation(limit = 20) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (deliveryId: number) => {
            await axiosApiInstance.patch(`/api/notifications/${deliveryId}/read`);
        },
        onMutate: async (deliveryId) => {
            await qc.cancelQueries({ queryKey: inboxKey });
            patchInboxCache(qc, limit, (items) =>
                items.map((item) =>
                    item.id === deliveryId ? { ...item, read: true } : item,
                ),
            );
            const prevUnread = qc.getQueryData<number>(unreadKey);
            if (typeof prevUnread === 'number' && prevUnread > 0) {
                qc.setQueryData(unreadKey, prevUnread - 1);
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
        onError: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
    });
}

export function useMarkAllNotificationsReadMutation(limit = 20) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await axiosApiInstance.post('/api/notifications/read-all');
        },
        onMutate: async () => {
            await qc.cancelQueries({ queryKey: inboxKey });
            patchInboxCache(qc, limit, (items) => items.map((item) => ({ ...item, read: true })));
            qc.setQueryData(unreadKey, 0);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
        onError: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
    });
}

export function useDeleteNotificationMutation(limit = 20) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (deliveryId: number) => {
            await axiosApiInstance.delete(`/api/notifications/${deliveryId}`);
        },
        onMutate: async (deliveryId) => {
            await qc.cancelQueries({ queryKey: inboxKey });
            let removedUnread = false;
            patchInboxCache(qc, limit, (items) =>
                items.filter((item) => {
                    if (item.id === deliveryId) {
                        removedUnread = !item.read;
                        return false;
                    }
                    return true;
                }),
            );
            if (removedUnread) {
                const prevUnread = qc.getQueryData<number>(unreadKey);
                if (typeof prevUnread === 'number' && prevUnread > 0) {
                    qc.setQueryData(unreadKey, prevUnread - 1);
                }
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
        onError: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
    });
}

export function useDeleteAllNotificationsMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await axiosApiInstance.post('/api/notifications/delete-all');
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
    });
}

export function useNotificationRecipientsQuery(enabled = true) {
    return useQuery({
        queryKey: recipientsKey,
        enabled,
        queryFn: async () => {
            const { data } = await axiosApiInstance.get<NotificationRecipient[]>(
                '/api/notifications/recipients',
            );
            return data;
        },
    });
}

export function useSendNotificationMutation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: SendNotificationPayload) => {
            await axiosApiInstance.post('/api/notifications/send', payload, {
                headers: { 'Content-Type': 'application/json' },
            });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: inboxKey });
            qc.invalidateQueries({ queryKey: unreadKey });
        },
    });
}
